import { useEffect, useState } from 'react'
import { displayTable } from '../utils/tableLabel'
import { useAuth } from '../context/AuthContext'
import { useOpenTickets } from '../context/OpenTicketsContext'
import { useMyWaiting } from '../hooks/useMyWaiting'
import { useKitchenReady } from '../hooks/useKitchenReady'
import { updateTicket } from '../services/orders'
import { markServed } from '../utils/serving'
import { CheckIcon, PencilIcon, XIcon } from '../components/icons'
import type { Order, OrderLine } from '../types'
import { sameLine } from '../utils/lineMath'
import './TrackPage.css'

const same = (a: OrderLine, b: OrderLine) => sameLine(a, b)

/**
 * Masa notları: garsonun kendi hatırlatması ("su istedi", "hesap istedi").
 *
 * Bu cihazda saklanıyor, kimseyle paylaşılmıyor, fişe gitmiyor, garsonun
 * cebindeki kağıdın yerine. Masa kapanınca not da siliniyor.
 */
const NOTES_KEY = 'adissoo.trackNotes'

function readNotes(): Record<string, string> {
  try {
    return JSON.parse(localStorage.getItem(NOTES_KEY) ?? '{}')
  } catch {
    return {}
  }
}

function useTableNotes(openIds: string[]) {
  const [notes, setNotes] = useState<Record<string, string>>(readNotes)

  const write = (next: Record<string, string>) => {
    setNotes(next)
    try {
      localStorage.setItem(NOTES_KEY, JSON.stringify(next))
    } catch {
      // Depolama yoksa not yalnızca bu oturumda kalır.
    }
  }

  // Kapanan masaların notları gitsin.
  const key = openIds.join(',')
  useEffect(() => {
    const open = new Set(openIds)
    const kept = Object.fromEntries(Object.entries(notes).filter(([id]) => open.has(id)))
    if (Object.keys(kept).length !== Object.keys(notes).length) write(kept)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key])

  const set = (id: string, text: string) => {
    const next = { ...notes }
    if (text.trim()) next[id] = text.trim()
    else delete next[id]
    write(next)
  }

  return { notes, set }
}

/**
 * Sipariş Takip: the waiter's own units not yet taken to the table.
 *
 * Marked as they go out, and gone from the list the moment they are, the
 * list is what is left to do, not a log. Oldest table first; each line says
 * how long it has waited, plainly, without colour.
 */
export function TrackPage() {
  const { tenantId } = useAuth()
  const { tables, now } = useMyWaiting()
  // Mutfak ekranında "hazır" denen ürünler: garson almaya gidebilir.
  const { readyOf, closeServed } = useKitchenReady(true)
  const { openTickets } = useOpenTickets()
  const { notes, set: setNote } = useTableNotes(openTickets.map((t) => t.id))
  /** Notu yazılan masa ve kutudaki metin. */
  const [editing, setEditing] = useState<{ id: string; text: string } | null>(null)
  const saveNote = () => {
    if (!editing) return
    setNote(editing.id, editing.text)
    setEditing(null)
  }

  // Always the live ticket from the subscription, so marking never writes back
  // a stale copy over a round someone added meanwhile. Silent: no slip prints.
  const serve = (ticket: Order, pick: (l: OrderLine) => boolean, count?: number) => {
    if (!tenantId) return
    const lines = markServed(ticket.lines, pick, count)
    updateTicket(tenantId, ticket.id, lines, ticket.itemCount, ticket.totalKurus, true)
    // Götürülen mutfakta beklemesin.
    void closeServed(ticket.id, lines)
  }

  const minutes = (since: number) => Math.max(0, Math.floor((now - since) / 60_000))

  if (tables.length === 0) {
    return (
      <div className="track">
        <div className="track__empty">
          <CheckIcon />
          <strong>Bekleyen sipariş yok</strong>
          <span>Aldığınız her şey masasına gitmiş.</span>
        </div>
      </div>
    )
  }

  return (
    <div className="track">
      {tables.map(({ ticket, lines, oldest }) => (
        <section key={ticket.id} className="track__table">
          <header className="track__head">
            <span className="track__name">{displayTable(ticket.tableName) ?? 'Adisyon'}</span>
            <span className="track__age">{minutes(oldest)} dk</span>
            <button
              type="button"
              className="track__note-btn"
              aria-label="Masaya not"
              title="Masaya not"
              onClick={() => setEditing({ id: ticket.id, text: notes[ticket.id] ?? '' })}
            >
              <PencilIcon />
            </button>
            <button
              type="button"
              className="track__all"
              onClick={() => serve(ticket, (l) => lines.some((w) => same(w.line, l)))}
            >
              Hepsini götürdüm
            </button>
          </header>
          {editing?.id === ticket.id ? (
            <form
              className="track__note-edit"
              onSubmit={(e) => {
                e.preventDefault()
                saveNote()
              }}
            >
              <input
                id={`note-${ticket.id}`}
                value={editing.text}
                onChange={(e) => setEditing({ id: ticket.id, text: e.target.value })}
                placeholder="Su istedi, hesap istedi…"
                maxLength={120}
              />
              <button type="submit">Kaydet</button>
            </form>
          ) : (
            notes[ticket.id] && (
              <div className="track__note">
                <button
                  type="button"
                  className="track__note-text"
                  onClick={() => setEditing({ id: ticket.id, text: notes[ticket.id] })}
                >
                  {notes[ticket.id]}
                </button>
                <button
                  type="button"
                  className="track__note-x"
                  aria-label="Notu sil"
                  onClick={() => setNote(ticket.id, '')}
                >
                  <XIcon />
                </button>
              </div>
            )
          )}
          <ul className="track__lines">
            {lines.map(({ line, waiting, since }) => (
              <li key={`${line.productId}|${line.note ?? ''}|${line.unitPriceKurus}`} className="track__line">
                <span className="track__qty">{waiting}×</span>
                <span className="track__item">
                  <span>
                    {line.name}
                    {line.note && <em> ({line.note})</em>}
                    {readyOf(ticket.id, line, waiting) > 0 && (
                      <b className="track__ready">
                        {readyOf(ticket.id, line, waiting) === waiting
                          ? 'Hazır, alınabilir'
                          : `${readyOf(ticket.id, line, waiting)} hazır`}
                      </b>
                    )}
                  </span>
                  {/* Birden fazla adette ilerleme: her Götürdüm bir parça
                      doldurur. Tek adette çubuk yok, söyleyecek bir şeyi yok. */}
                  {line.qty > 1 && (
                    <span className="track__progress" aria-label={`${line.qty - waiting}/${line.qty} götürüldü`}>
                      <span className="track__bar">
                        {Array.from({ length: line.qty }, (_, i) => (
                          <i key={i} className={i < line.qty - waiting ? 'is-done' : ''} />
                        ))}
                      </span>
                      <span className="track__count">
                        {line.qty - waiting}/{line.qty}
                      </span>
                    </span>
                  )}
                </span>
                <span className="track__min">{minutes(since)} dk</span>
                <button type="button" className="track__done" onClick={() => serve(ticket, (l) => same(l, line), 1)}>
                  <CheckIcon />
                  Götürdüm
                </button>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  )
}
